// noinspection JSUnresolvedVariable

import Uppy from "@uppy/core";
import Dashboard from "@uppy/dashboard";
import XHRUpload from "@uppy/xhr-upload";
import handleAjaxError from "../helpers/handleAjaxError";
import Axios from "axios";
import {hideLoading, showLoading} from "../helpers/loading";
import createElement from "../helpers/createElement";
import {randomString} from "../helpers/random";
import {waitUntil} from "../helpers/waitUntil";

/**
 * @typedef {Object} uppyExtraProperties
 * @property {function(): number[]} getFilesForSubmission Returns an array containing the ids of the files uploaded
 * @property {function(): void} fillParentContainer Expands the dashboard to fill parent container
 * @property {Element} parentContainer
 * @typedef {uppyExtraProperties & Uppy} UppyExtended
 */

/**
 * @typedef {Object} UppyFileInstance
 * @property {number} id Returns an array containing the ids of the files uploaded
 */

/**
 * @param options An object containing options for uppy
 * @param {string|Element} options.target CSS Selector for the dashboard container
 * @param {boolean} [options._allowDeletion] Allows removal of existing files
 * @param {?string} endpoint The endpoint for the XHR Plugin
 * @param {
 * 		{
 *			id: number,
 *			type: string,
 *			original_name: string,
 * 		 	download_url: string,
 * 		 	thumbnail_url: string,
 * 		}[]
 * } [files] an optional array containing existing files
 * @return {UppyExtended} uppyInstance
 */
function setupFileUploader(options, endpoint = null, files = null) {
	const uppyDashboardContainer = typeof options.target === 'string' ?
		document.querySelector(options.target) : // Resolve string selector
		options.target; // Use directly
	uppyDashboardContainer.classList.add('loadable');

	const defaultOptions = {
		trigger: '.upload-uppy-files',
		target: null,
		inline: true,
		showProgressDetails: true,
		showRemoveButtonAfterComplete: true,
		proudlyDisplayPoweredByUppy: false,
		hideCancelButton: true,
		disableStatusBar: true,
		height: 240,
		width: 10000,

		// Custom settings
		_allowDeletion: true,
	}

	const finalOptions = {
		...defaultOptions,
		...options,
	}

	// Setup Uppy
	let uppyInstance = new Uppy({
		debug: true,
		restrictions: {
			maxFileSize: 20000000,
		},
		autoProceed: false,
	}).use(Dashboard, finalOptions);

	if (endpoint) {
		// noinspection JSUnusedGlobalSymbols,JSCheckFunctionSignatures
		uppyInstance.use(XHRUpload, {
			endpoint: endpoint,
			headers: {
				'X-CSRF-Token': $("head").find("meta[name=csrf]").attr("content"),
			},
			getResponseError: (responseText) => {
				const responseJson = JSON.parse(responseText);
				handleAjaxError({
					response: {
						data: responseJson
					}
				});
			},
		})
	}

	// Add existing files
	if (files) {
		// Show loader while files are fetched
		showLoading(uppyDashboardContainer)

		let thumbnailPromises = [];

		/**
		 * For existing files we need to get the actual
		 * file contents, so the uppy dashboard can display
		 * the correct thumbnails
		 */

		// Prepare all the files thumbnails promises
		files.forEach(file => {
			// noinspection JSUnresolvedVariable
			thumbnailPromises.push(
				Axios.get(file.thumbnail_url, {
					responseType: 'blob'
				}).then(response => {
					return {
						response,
						file
					}
				}).catch(error => {
					return {
						error,
						file,
					}
				})
			);
		})

		// Wait for all the request to finish
		Axios.all(thumbnailPromises)
			.then(results => {
				// Add the files to the uppy dashboard, with their thumbnails
				results.forEach(result => {
					const meta = {
						serverId: result.file.id,
						serverFileObject: result.file,
						// Uppy doesn't like duplicate filenames
						// so we trick it by adding a fake relative
						// path to each existing file
						relativePath: randomString(4),
					}

					if (result.response) {
						// Thumbnail retrieved
						uppyInstance.addFile({
							name: result.file.original_name,
							type: result.file.type,
							data: result.response.data,

							meta: meta
						});
					} else {
						// Error retrieving a thumbnail, use an empty blob
						uppyInstance.addFile({
							name: result.file.original_name,
							type: result.file.type,
							data: new Blob(['']),
							meta: meta
						});
					}
				});

				uppyInstance.getFiles().forEach(file => {
					// Mark all the files as completed and uploaded
					uppyInstance.setFileState(file.id, {
						progress: {uploadComplete: true, uploadStarted: true}
					})

					// Wait until elements have been added to the DOM
					waitUntil(() => getDashboardElement(uppyDashboardContainer, file.id))
						.then(() => {
							if (!finalOptions._allowDeletion) {
								// Hack, wait a bit as uppy adds the delete button again
								setTimeout(() => {
									// Disallow removal
									const dashboardElement = getDashboardElement(uppyDashboardContainer, file.id);
									const deleteButton = dashboardElement.querySelector('.uppy-Dashboard-Item-action--remove');
									deleteButton.parentElement.removeChild(deleteButton);

								}, 32)
							}

						})
					// Add a download button to existing files
					addDownloadButtons(uppyDashboardContainer, file);

					replaceFileSize(uppyDashboardContainer, file);
				})

				uppyInstance.setOptions({
					autoProceed: true,
				});

				hideLoading(uppyDashboardContainer);
			})
	} else {
		// Empty dashboard, allow uploads immediately
		uppyInstance.setOptions({
			autoProceed: true,
		});
	}

	// Add events to keep track of new files
	uppyInstance.on('upload-success', file => {
		// Add 'new upload' class once element has been added to DOM
		waitUntil(() => getDashboardElement(uppyDashboardContainer, file.id), {
			timeoutAfter: 10000,
			msBetweenAttempts: 16,
		})
			.then(() => {
				const fileContainer = getDashboardElement(uppyDashboardContainer, file.id);

				fileContainer.classList.add('new-upload');
				fileContainer.title = 'New Upload';
			})
	})

	uppyInstance._parentContainer = uppyDashboardContainer;
	uppyInstance.getFilesForSubmission = getFilesForSubmission;
	uppyInstance.fillParentContainer = fillParentContainer;

	// noinspection JSValidateTypes
	return uppyInstance;

	/** Methods */

}

/**
 * Returns an array of the uploader files, ready to be submitted to the server
 * @returns {UppyFileInstance[]}
 */
function getFilesForSubmission() {
	// Get uploaded files
	// noinspection JSUnresolvedVariable
	return this.getFiles().filter(file => {
		// If it was a successful upload or an already existing file
		return (file.response && file.response.status === 200) || (file.meta.serverId !== undefined);
	}).map(file => {
		// Get the id, either from an existing file or a fresh upload
		let id;

		if (file.meta && file.meta.serverId) {
			id = file.meta.serverId;
		} else {
			id = file.response.body.id;
		}

		return {
			id,
		}
	});
}

/**
 * Returns the DOM element belonging to an uppy file
 * @param dashboardContainer
 * @param id
 */
function getDashboardElement(dashboardContainer, id) {
	let foundElement = null;
	dashboardContainer.querySelectorAll('.uppy-Dashboard-Item').forEach(element => {
		// What the hell Uppy
		// We need to replace all - into _ to compare the ids correctly
		const elementId = element.id.replace('-', '_');
		const searchId = (`uppy_${id}`).replace('-', '_');

		if (elementId === searchId) {
			foundElement = element;
		}
	})

	return foundElement;
}

/**
 * Attempts to add a download button, waiting until the dom element is present
 *
 * @param dashboardContainer
 * @param file
 * @param tries
 * @param maxTries
 */
function addDownloadButtons(dashboardContainer, file, tries = 0, maxTries = 5) {
	if (file.meta.serverFileObject && file.meta.serverFileObject.download_url) {
		const fileElement = getDashboardElement(dashboardContainer, file.id);
		const url = file.meta.serverFileObject.download_url;

		if (!fileElement) {
			// Element not found, wait a while and attempt again
			if (tries <= maxTries) {
				setTimeout(
					() => addDownloadButtons(dashboardContainer, file, tries + 1, maxTries),
					32
				)
			}
			return;
		}

		// Attachment download button
		fileElement.appendChild(createElement(`
			<a href="${url}" target="_blank" class="btn btn-sm btn-primary download-button">
				<i class="mdi mdi-download"></i>
			</a>
		`));

		// Inline download button
		fileElement.querySelector('.uppy-Dashboard-Item-preview').appendChild(createElement(`
			<a href="${url}?inline=true" target="_blank" class="inline-download-button">
				<i class="mdi mdi-card-search"></i>
			</a>
		`));

	}
}

/**
 * Attempts to replace the file size tag by the one provided from the server,
 * waiting until the dom element is present
 *
 * @param dashboardContainer
 * @param file
 */
function replaceFileSize(dashboardContainer, file) {
	if (file.meta.serverFileObject && file.meta.serverFileObject.download_url) {
		waitUntil(() => {
			// Wait until element is on dom
			return getDashboardElement(dashboardContainer, file.id);
		}, {
			timeoutAfter: 5000,
			msBetweenAttempts: 500
		}).then(() => {
			const fileElement = getDashboardElement(dashboardContainer, file.id);

			const sizeTag = fileElement.querySelector('.uppy-Dashboard-Item-statusSize');
			if (sizeTag) {
				sizeTag.innerHTML = file.meta.serverFileObject.file_size_for_humans;
			}
		}).catch(console.log) // Log error but keep working
	}
}

/**
 * Checks if any file uploader inside domElement its
 * currently uploading something and displays a warning
 * about it if required. Returns a Promise that resolves if no
 * upload is occurring or the warning accepted
 *
 * @param domElement {Element}
 */
function checkForPendingUploads(domElement) {
	return new Promise((resolve) => {
		// Check element for current uploads
		const uploadInProgress = domElement.querySelector('.uppy-Dashboard-Item.is-inprogress');

		if (uploadInProgress) {
			const message = `
					One or more files are being uploaded, please wait for the upload to finish.
					<br/>
					If you continue anyway all uploaded files may not be saved.
			`;

			return Swal.fire({
				title: 'File upload in progress',
				html: message,
				icon: 'info',
				showCancelButton: true,
				confirmButtonColor: '#3085d6',
				cancelButtonColor: '#d33',
				confirmButtonText: 'Continue Anyway',
				cancelButtonText: 'Cancel',
			}).then(result => {
				if (result.isConfirmed) {
					resolve();
				}
			})
		} else {
			// Resolve immediately
			resolve();
		}
	})
}

function fillParentContainer() {
	const dashboardInner = this._parentContainer.querySelector('.uppy-Dashboard-inner');

	const parentHeight = this._parentContainer.clientHeight;

	dashboardInner.style.height = `${parentHeight}px`;
}

export {setupFileUploader, checkForPendingUploads}
